/**
 * This browser's own microphone, in standalone 30-second files.
 *
 * ── Why the recorder is restarted rather than sliced ────────────────────────
 * `MediaRecorder.start(timeslice)` hands back a blob every `timeslice`
 * milliseconds, and all but the first are *fragments*: the container header is
 * in blob 0 and nothing else can be decoded on its own. The server transcribes
 * each chunk independently, so every chunk has to be a file. Restarting the
 * recorder per chunk is what buys that, at the cost of a few milliseconds of
 * audio at each boundary — which is the trade `docs/calls-and-meetings.md`
 * records, and the reason this does not use `timeslice`.
 *
 * ── Offsets are the meeting's clock, not this recorder's ────────────────────
 * `offsetMs` is measured from the meeting's live start (`since` on the roster
 * answer), the same origin `mark()` uses. Measuring from the moment recording
 * began would put every participant on a different clock — somebody who joined
 * ten minutes late would have their first sentence transcribed at 0:00 — and
 * the transcript merges everybody by `startMs`.
 *
 * Each participant records only itself. Nothing here touches a remote track.
 */

import { currentMeetingToken, GUEST_HEADER } from '../api';

/** The server refuses anything larger, and 30 s of Opus is about 120 KB. */
export const CHUNK_MS = 30_000;

/** The first of these the browser admits to. Chrome and Firefox take the
 *  first; Safari takes the last. */
export const CHUNK_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg'
] as const;

/** `content-type` on the upload is the container, without the codec parameter:
 *  the route allows `audio/webm` and `audio/ogg` and nothing beside them. */
export function containerType(mimeType: string): string {
  const base = mimeType.split(';')[0]?.trim() ?? '';
  return base === 'audio/ogg' ? 'audio/ogg' : 'audio/webm';
}

/** The slice of `MediaRecorder` this file uses, so a test can stand in for it
 *  without a DOM. */
export interface RecorderLike {
  start(): void;
  stop(): void;
  readonly state: string;
  readonly mimeType: string;
  ondataavailable: ((event: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: ((event: unknown) => void) | null;
}

export type RecorderFactory = (track: MediaStreamTrack) => RecorderLike;

export function browserRecorderFactory(): RecorderFactory {
  const supported = CHUNK_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
  return (track) =>
    new MediaRecorder(
      new MediaStream([track]),
      supported ? { mimeType: supported } : undefined
    ) as RecorderLike;
}

export interface Chunk {
  seq: number;
  offsetMs: number;
  durationMs: number;
  contentType: string;
  blob: Blob;
}

export type UploadChunk = (chunk: Chunk) => Promise<void>;

/**
 * One chunk, to the meeting it belongs to.
 *
 * Raw `fetch` rather than `api()`: the body is audio, and `api()` labels every
 * body it is given as JSON.
 */
export function uploadToMeeting(
  meetingId: string,
  fetcher: typeof fetch = fetch
): UploadChunk {
  const path = `/api/relay/meetings/${encodeURIComponent(meetingId)}/recording/chunks`;
  return async (chunk) => {
    // A host starts the recording for everybody, so a guest's browser uploads
    // its own microphone too — and a guest is authenticated by the header, not
    // by a cookie. `api()` would add it; this does not use `api()` because the
    // body is audio and `api()` labels every body it is given as JSON.
    const guest = currentMeetingToken();
    const res = await fetcher(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': chunk.contentType,
        'x-chunk-seq': String(chunk.seq),
        'x-chunk-offset-ms': String(chunk.offsetMs),
        'x-chunk-duration-ms': String(chunk.durationMs),
        ...(guest ? { [GUEST_HEADER]: guest } : {})
      },
      body: chunk.blob
    });
    if (!res.ok) throw new Error(`The recording could not be uploaded (${res.status}).`);
  };
}

export interface ChunkRecorderOptions {
  track: MediaStreamTrack;
  /** The meeting's live start, in the same clock `now()` reads. */
  sinceMs: number;
  upload: UploadChunk;
  createRecorder: RecorderFactory;
  onError?: (error: Error) => void;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export class ChunkRecorder {
  private readonly options: ChunkRecorderOptions;
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  private recorder: RecorderLike | null = null;
  private timer: unknown = null;
  private running = false;
  private nextSeq = 0;
  /** Uploads are chained rather than raced: two 8 MiB bodies in flight on a
   *  thin connection is where a call's own audio starts to stutter. */
  private uploads: Promise<void> = Promise.resolve();

  constructor(options: ChunkRecorderOptions) {
    this.options = options;
    this.now = options.now ?? (() => Date.now());
    this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = options.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  get active(): boolean {
    return this.running;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.slice();
  }

  /** Stops at the next boundary the caller asked for — which is now: the
   *  current slice is closed, and its blob is uploaded like any other. */
  async stop(): Promise<void> {
    if (!this.running) return;
    this.running = false;
    if (this.timer !== null) {
      this.clearTimer(this.timer);
      this.timer = null;
    }
    const recorder = this.recorder;
    this.recorder = null;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    await this.uploads;
  }

  private slice(): void {
    const seq = this.nextSeq++;
    const startedAt = this.now();
    const recorder = this.options.createRecorder(this.options.track);
    this.recorder = recorder;

    recorder.ondataavailable = ({ data }) => {
      if (!data || data.size === 0) return;
      this.send({
        seq,
        offsetMs: Math.max(0, Math.round(startedAt - this.options.sinceMs)),
        durationMs: Math.max(0, Math.round(this.now() - startedAt)),
        contentType: containerType(recorder.mimeType),
        blob: data
      });
    };

    // The next slice starts from the old one's end rather than from a second
    // timer, so a `stop()` that arrives mid-slice cannot be followed by a
    // recorder nobody is holding.
    recorder.onstop = () => {
      if (this.running) this.slice();
    };

    recorder.onerror = () => {
      this.fail(new Error('The recording stopped.'));
    };

    recorder.start();
    this.timer = this.setTimer(() => {
      if (recorder.state !== 'inactive') recorder.stop();
    }, CHUNK_MS);
  }

  private send(chunk: Chunk): void {
    this.uploads = this.uploads
      .then(() => this.options.upload(chunk))
      .catch((error: unknown) => {
        this.fail(error instanceof Error ? error : new Error(String(error)));
      });
  }

  private fail(error: Error): void {
    this.options.onError?.(error);
  }
}
